import React, { useState, useEffect } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { clusterService, machineService } from '../services/api';
import ClusterManager from '../components/ClusterManager';
import ClusterCard from '../components/ClusterCard';
import { useAuth } from '../contexts/AuthContext';

function ClustersPage() {
  const { hasPermission } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [machines, setMachines] = useState([]);
  const [showClusterManager, setShowClusterManager] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [editingCluster, setEditingCluster] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchMachines();
      await fetchClusters();
      setLoading(false);
    };
    loadData();
  }, []);

  const fetchMachines = async () => {
    try {
      const data = await machineService.getAll();
      setMachines(data);
    } catch (err) {
      showAlert('Failed to fetch machines', 'danger');
    }
  };

  const fetchClusters = async () => {
    try {
      const data = await clusterService.getAll();
      const clustersWithCounts = await Promise.all(
        data.map(async cluster => {
          const details = await clusterService.getById(cluster.id);
          return {
            ...cluster,
            machine_count: details.machines.length
          };
        })
      );
      setClusters(clustersWithCounts);
    } catch (err) {
      showAlert('Failed to fetch clusters', 'danger');
    }
  };

  const handleClusterPowerAction = async (clusterId, action, password) => {
    try {
      await clusterService.performAction(clusterId, action, password || 'admin123');
      showAlert(`Cluster ${action} initiated successfully! 🎉`, 'success');
      return true;
    } catch (err) {
      showAlert(`Failed to ${action} cluster. Please try again.`, 'danger');
      return false;
    }
  };

  const handleEditCluster = (cluster) => {
    setEditingCluster(cluster);
    setShowClusterManager(true);
  };

  const handleDeleteCluster = async (clusterId) => {
    if (window.confirm('⚠️ Are you sure you want to delete this cluster? This action cannot be undone.')) {
      try {
        await clusterService.delete(clusterId);
        fetchClusters();
        showAlert('Cluster deleted successfully! 🗑️', 'success');
      } catch (err) {
        showAlert('Failed to delete cluster. Please try again.', 'danger');
      }
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  return (
    <div className="py-4" style={{ minHeight: '100vh', paddingLeft: '2rem', paddingRight: '2rem' }}>
      
      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h1 className="mb-1 text-theme-main" style={{ fontSize: '1.75rem', fontWeight: 700 }}>
            <i className="fas fa-layer-group me-2 text-primary"></i>
            Cluster Management
          </h1>
          <p className="text-theme-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Group your network assets together to deploy power commands in bulk
          </p>
        </div>
        {hasPermission('manage_assets') && (
          <div>
            <Button 
              onClick={() => {
                setEditingCluster(null);
                setShowClusterManager(true);
              }} 
              className="btn-premium-primary d-flex align-items-center gap-2"
            >
              <i className="fas fa-plus-circle"></i>
              Add Cluster
            </Button>
          </div>
        )}
      </div>

      {alert.show && (
        <Alert 
          variant={alert.variant} 
          onClose={() => setAlert({ ...alert, show: false })} 
          dismissible
          className="mb-4 border-0"
          style={{
            backgroundColor: alert.variant === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)',
            color: alert.variant === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
            borderRadius: 'var(--radius-md)'
          }}
        >
          {alert.message}
        </Alert>
      )}

      <div>
        {loading ? (
          <div className="glass-card text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted mb-0">Loading clusters...</p>
          </div>
        ) : clusters.length === 0 ? (
          <div className="glass-card text-center py-5 d-flex flex-column align-items-center justify-content-center p-5">
            <i className="fas fa-layer-group text-theme-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h3 className="text-theme-main mb-2" style={{ fontSize: '1.25rem' }}>No Clusters Found</h3>
            <p className="text-theme-muted mb-4" style={{ maxWidth: '360px', fontSize: '0.9rem' }}>
              Create logical groups of workstations (e.g. by room, team, or function) to control them simultaneously.
            </p>
            {hasPermission('manage_assets') && (
              <Button 
                onClick={() => {
                  setEditingCluster(null);
                  setShowClusterManager(true);
                }}
                className="btn-premium-primary"
              >
                <i className="fas fa-plus mr-2"></i>
                Create Your First Cluster
              </Button>
            )}
          </div>
        ) : (
          <div className="row g-4">
            {clusters.map((cluster) => (
              <div key={cluster.id} className="col-12 col-md-6 col-lg-4">
                <ClusterCard
                  cluster={cluster}
                  onClusterAction={handleClusterPowerAction}
                  onEdit={handleEditCluster}
                  onDelete={handleDeleteCluster}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <ClusterManager
        show={showClusterManager}
        onHide={() => setShowClusterManager(false)}
        machines={machines}
        onClusterCreated={fetchClusters}
        editCluster={editingCluster}
      />
    </div>
  );
}

export default ClustersPage;